<template>
  <div class="acc-docs-list">
    <accounting-docs-list-desktop-bridge v-if="isDesktopListLayout" />
    <accounting-docs-list-mobile v-else />
    <print-studio
      v-model="printStudio"
      report="accounting-docs"
      :title="$t($route.name)"
      :subtitle="printSubtitle"
      :columns="printColumns"
      :source="printSource"
      @error="serverErrorHandeler"
    />
    <accounting-doc-view
      v-model="docViewOpen"
      :item="docViewItem"
      @edit="docViewOpen = false; edit($event)"
      @copied="copied"
      @error="serverErrorHandeler"
    />
    <!--    filter dialog-->
    <v-dialog
      v-model="filter"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="960"
      scrollable
      content-class="acf-wrap"
    >
      <div class="acf" :style="proListRootStyle" dir="rtl">
        <header class="acf-head">
          <span class="acf-head__icon"><v-icon size="20">mdi-filter-variant</v-icon></span>
          <div class="acf-head__titles">
            <div class="acf-head__title">{{ $t("accFilterTitle") }}</div>
            <div class="acf-head__sub">
              <template v-if="activeFilterChips.length">
                <span class="acf-badge">{{ faNum(activeFilterChips.length) }}</span>
                {{ $t("accFilterActive") }}
              </template>
              <template v-else>{{ $t("accFilterNone") }}</template>
            </div>
          </div>
          <button type="button" class="acf-x" :title="$t('close')" @click="filter = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </header>

        <div class="acf-body">
          <!-- Date & time -->
          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-calendar-range</v-icon>
              {{ $t("accFilterDateTime") }}
            </div>
            <div class="acf-grid acf-grid--4">
              <div>
                <v-text-field
                  id="fromId"
                  v-model="fromDate"
                  :label="$t('from') + ' ' + $t('date')"
                  prepend-inner-icon="mdi-calendar-start"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="fromDate"
                  element="fromId"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  auto-submit
                  :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                  @close="setDate"
                />
              </div>
              <div>
                <v-text-field
                  id="toId"
                  v-model="toDate"
                  :label="$t('until') + ' ' + $t('date')"
                  prepend-inner-icon="mdi-calendar-end"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="toDate"
                  element="toId"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  :min="fromDate"
                  auto-submit
                  :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                  @close="setDate"
                />
              </div>
              <div>
                <v-text-field
                  id="fromTime"
                  v-model="fromTime"
                  :label="$t('from-hour')"
                  prepend-inner-icon="mdi-clock-start"
                  outlined
                  dense
                  clearable
                  readonly
                  hide-details
                />
                <date-picker
                  v-model="fromTime"
                  element="fromTime"
                  type="time"
                  :locale="$store.state.date_locale"
                  :format="'HH:mm'"
                  :display-format="'HH'"
                  auto-submit
                  :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                  @close="setDate"
                />
              </div>
              <div>
                <v-text-field
                  id="toTime"
                  v-model="toTime"
                  :label="$t('to-hour')"
                  prepend-inner-icon="mdi-clock-end"
                  outlined
                  dense
                  clearable
                  readonly
                  hide-details
                />
                <date-picker
                  v-model="toTime"
                  element="toTime"
                  type="time"
                  :locale="$store.state.date_locale"
                  :format="'HH:mm'"
                  :display-format="'HH'"
                  auto-submit
                  :color="$vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                  @close="setDate"
                />
              </div>
            </div>
          </section>

          <!-- Number & amount -->
          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-pound-box-outline</v-icon>
              {{ $t("accFilterNumberAmount") }}
            </div>
            <div class="acf-grid acf-grid--4">
              <v-text-field
                v-model="fromNumber"
                :label="$t('from') + ' ' + $t('number')"
                outlined
                dense
                clearable
                hide-details
              />
              <v-text-field
                v-model="toNumber"
                :label="$t('until') + ' ' + $t('number')"
                outlined
                dense
                clearable
                hide-details
              />
              <money
                v-model="fromAmount"
                :label="$t('fromAmount')"
                :dense="true"
                :outlined="true"
                :solo="false"
                :clearable="true"
                value-when-is-empty="0"
                :options="options"
                class="dense"
              />
              <money
                v-model="toAmount"
                :label="$t('toAmount')"
                :dense="true"
                :outlined="true"
                :solo="false"
                :clearable="true"
                value-when-is-empty="0"
                :options="options"
                class="dense"
              />
            </div>
          </section>

          <!-- Accounts -->
          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-account-cash-outline</v-icon>
              {{ $t("accDocListFilterAccounts") }}
            </div>
            <div class="acf-grid acf-grid--3">
              <autocomplete v-model="given" title="given" type="given" />
              <autocomplete v-model="fin4" title="detail" type="detail" :multiple="true" />
              <autocomplete v-model="fin5" title="fin5" type="detail" />
              <autocomplete v-model="fin6" title="fin6" type="detail" :multiple="true" />
              <autocomplete v-model="branch" title="branch" type="branch" :filter="true" :multiple="true" />
              <autocomplete
                v-model="registerar"
                :title="$t('registerar')"
                type="partners"
                :multiple="true"
                :filter="true"
              />
            </div>
          </section>

          <!-- Type & status -->
          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-shape-outline</v-icon>
              {{ $t("accDocListFilterCategory") }}
            </div>
            <div class="acf-grid acf-grid--3">
              <v-select
                v-model="type"
                :label="$t('natureAccount')"
                :items="typeItems"
                dense
                outlined
                multiple
                small-chips
                clearable
                hide-details
                item-value="value"
                item-text="text"
              />
              <v-select
                v-model="submitDocWay"
                :label="$t('submitDocWay')"
                :items="submitDocWays"
                dense
                outlined
                multiple
                clearable
                hide-details
                item-value="value"
                item-text="label"
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0" class="acf-sel">{{ item.label }}</span>
                  <span v-if="index === 1" class="acf-sel acf-sel--more">
                    +{{ faNum(submitDocWay.length - 1) }}
                  </span>
                </template>
              </v-select>
              <v-select
                v-model="lockStatus"
                :label="$t('lockStatusDocuments')"
                :items="lockStatusItems"
                dense
                outlined
                clearable
                hide-details
                item-value="value"
                item-text="text"
              />
              <status v-model="sellStatus" :hideconfig="true" />
              <tag v-model="tag" />
            </div>
          </section>
        </div>

        <footer class="acf-foot">
          <button type="button" class="acf-btn acf-btn--primary" @click="getItems">
            <v-icon size="16">mdi-filter-check-outline</v-icon>
            {{ $t("accFilterApply") }}
          </button>
          <button
            type="button"
            class="acf-btn acf-btn--ghost"
            :disabled="!activeFilterChips.length"
            @click="resetFilters"
          >
            <v-icon size="16">mdi-filter-remove-outline</v-icon>
            {{ $t("accDocListClearFilters") }}
          </button>
          <span class="acf-spacer" />
          <button type="button" class="acf-btn" @click="filter = false">{{ $t("cancel") }}</button>
        </footer>
      </div>
    </v-dialog>
    <!--    tasl dialog-->
    <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="taskDialog"
      width="90%"
    >
      <v-card>
        <v-col class="d-flex">
          <v-btn icon color="grey darken-2" @click="taskDialog = false">
            <v-icon class="close-hover" large> mdi-close </v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t("task") }}
          </p>
        </v-col>
        <v-row class="ma-0">
          <task
            task-type="account"
            :ref-id="refId"
            :task-id="taskId"
            @close="taskDialog = false"
          />
        </v-row>
        <v-card-actions />
      </v-card>
    </v-dialog>
    <!--    delete dialog-->
    <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="deletDialog"
      width="400"
    >
      <v-card>
        <v-col>
          <v-btn icon class="close-hover" @click="deletDialog = false">
            <v-icon color="grey" large> mdi-close </v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <v-img class="mx-auto" width="200" src="@/assets/stop.svg" />
        </v-col>
        <v-col class="mt-3 pr-7">
          <p class="mx-auto font-weight-medium">
            {{ $t("are-you-sure-delete-this-case") }}
          </p>
        </v-col>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="6">
              <v-btn v-if="selected.length > 0" color="primary" @click="removegroup">
                {{ $t("remove") }}
              </v-btn>
              <v-btn v-if="selected.length == 0" color="primary" @click="remove">
                {{ $t("remove") }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="grey"
                outlined
                class="float-left"
                @click="deletDialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!--    print dialog-->
    <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="printDialog"
      width="40%"
    >
      <v-card>
        <v-col class="d-flex pb-0">
          <v-btn icon color="grey darken-2" @click="printDialog = false">
            <v-icon class="close-hover" large> mdi-close </v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t("print") }}
          </p>
        </v-col>
        <v-row class="ma-0">
          <v-col class="text-right">
            <v-radio-group v-model="printType">
              <template v-slot:label>
                <div class="text-right">
                  {{ $t("chooseTemplate") }}
                </div>
              </template>
              <v-radio value="document">
                <template v-slot:label>
                  <div>{{ $t("totalGivenDetails") }}</div>
                </template>
              </v-radio>
              <v-radio value="document1">
                <template v-slot:label>
                  <div>{{ $t("totalGiven") }}</div>
                </template>
              </v-radio>
              <v-radio value="document2">
                <template v-slot:label>
                  <div>{{ $t("givenDetails") }}</div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-card-actions>
          <v-btn color="primary" :loading="loading" @click="openPrint">
            {{ $t("print") }}
          </v-btn>
          <v-spacer />
          <v-btn color="grey" @click="printDialog = !printDialog">
            {{ $t("close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!--    upload dialog-->
    <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="uploadDialog"
      width="500"
    >
      <v-card>
        <v-col>
          <v-btn icon class="close-hover" @click="uploadDialog = false">
            <v-icon color="grey" large> mdi-close </v-icon>
          </v-btn>
        </v-col>
        <v-col class="mt-0 pt-0 pr-7">
          <upload
            type-prop="accountingDocument"
            :get-item="uploadDialog"
            @close="uploadDialog = false"
          />
        </v-col>
        <v-card-actions>
          <v-row class="ma-0" />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="listPrintDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="90%"
    >
      <v-card>
        <v-col class="d-flex pb-0">
          <v-btn icon color="grey darken-2" @click="listPrintDialog = false">
            <v-icon class="close-hover" large> mdi-close </v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t("print") }}
          </p>
        </v-col>
        <v-row class="ma-0">
          <print
            :header-prop="headers"
            :items-prop="items"
            :type-prop="type"
            :title="$t($route.name)"
          />
        </v-row>
        <v-card-actions />
      </v-card>
    </v-dialog>
    <!--permission-->
    <v-dialog
      v-model="askPermissionDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="300"
    >
      <v-card class="pa-5">
        <v-container class="pa-0 d-flex justify-center">
          <v-icon size="70" color="warning">mdi-alert-circle-outline</v-icon>
        </v-container>
        <v-card-text class="pa-0 text-body-1 text-center my-3">
          {{ $t("are you sure you want to convert permanent to temporary") }}
          {{ docId ? "(" + docId + ")" : docIds ? "(" + docIds.join(", ") + ")" : "" }}
          {{ $t("are you sure ?") }}
        </v-card-text>
        <v-container class="pa-0 d-flex justify-center align-center gap-2">
          <v-btn color="primary" height="32" @click="resolveConvertFunc">
            {{ $t("yes") }}
          </v-btn>
          <v-btn color="error" height="32" @click="askPermissionDialog = false">
            {{ $t("no") }}
          </v-btn>
        </v-container>
      </v-card>
    </v-dialog>

    <!--aggregate-->
    <aggregate-dialog
      v-model="aggregateDialog"
      :loading="aggregateLoading"
      @print="openAggregatePrint"
    />
    <dialog-component
      :dialog-prop="innerComponent"
      :data-prop="dialogData"
      @close="innerComponent = false"
    />
    <v-snackbar
      v-model="snakbar"
      :color="snakbarColor ? snakbarColor : 'blue'"
      top
      style="z-index: 1000000000000 !important"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = !snakbar">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Task from "../task/task";
import General from "../../../../Mixins/general";
import Upload from "../../components/general/upload/upload";
import Print from "../../components/general/Print/Index";
import AggregateDialog from "./partial/AggregateDialog";
import {
  applyColumnSettings,
  loadColumnSettings,
} from "@/plugins/columnSettings/columnSettings";
import { reorderPinnedHeaders } from "@/plugins/columnPin/columnPin";
import AccountingDocsListDesktopBridge from "./layouts/desktop/AccountingDocsListDesktopBridge.vue";
import ProList from "@/Mixins/proList";
import AccountingDocView from "./layouts/AccountingDocView.vue";
import PrintStudio from "@/modules/print/PrintStudio.vue";
import AccountingDocsListMobile from "./layouts/mobile/AccountingDocsListMobile.vue";
export default {
  name: "AccountingList",
  provide() {
    return { accountingDocsList: this };
  },
  components: {
    AccountingDocsListDesktopBridge,
    AccountingDocsListMobile,
    AccountingDocView,
    PrintStudio,
    Tag: () => import("../../components/general/Tag/tag"),
    Status: () => import("../../components/general/Status/status"),
    dialogComponent: () => import("../../components/general/dialog/index"),
    AggregateDialog,
    Print,
    Upload,
    Task,
  },
  mixins: [General, ProList],
  props: ["input", "refId", "mobileShow"],
  data: () => ({
    excelItems: [],
    selected: [],
    pageCount: 1,
    page: 1,
    printDialog: false,
    aggregateDialog: false,
    aggregateLoading: false,
    printItems: [],
    printType: "document2",
    listPrintDialog: false,
    deletDialog: false,
    conflictingDocuments: null,
    registerar: null,
    fromNumber: null,
    toNumber: null,
    fromDate: null,
    toDate: null,
    snakbarColor: null,
    uploadDialog: null,
    dialogData: null,
    filter: false,
    model: null,
    from: 1,
    taskDialog: null,
    tag: null,
    temp: null,
    deleteItem: null,
    sellStatus: null,
    items: [],
    sortBy: [],
    dialog: null,
    timer: 0,
    totalItems: 0,
    snakbar: false,
    taskId: null,
    snakbarText: null,
    loading: true,
    options: {
      itemsPerPage: 5,
    },
    // Separate from General mixin money `options` (beforeMount overwrites it).
    tableOptions: {
      page: 1,
      itemsPerPage: 10,
    },
    branch: null,
    lockStatus: null,
    docViewOpen: false,
    printStudio: false,
    docViewItem: null,
    type: null,
    typeForm: "document",
    headers: null,
    typeItems: null,
    search: null,
    given: null,
    fin4: null,
    fin5: null,
    fin6: null,
    fromAmount: null,
    toAmount: null,
    fromTime: null,
    toTime: null,
    askPermissionDialog: false,
    docId: null,
    docIds: null,
    submitDocWay: [],
  }),
  computed: {
    isDesktopListLayout() {
      if (this.mobileShow) return true;
      return this.$vuetify.breakpoint.width >= 800;
    },
    proListKey() {
      return this.accountingListColumnPersistKey;
    },
    proListLocked() {
      return this.accountingListLockedColumns;
    },
    printColumns() {
      var app = this;
      var user = function (r) {
        return r.user ? r.user.name || [r.user.firstname, r.user.lastname].filter(Boolean).join(" ") : "";
      };
      return [
        { key: "id", label: app.$t("id"), format: "number", width: 0.8, defaultVisible: false },
        { key: "number", label: app.$t("document-number"), format: "number", width: 0.9 },
        { key: "turning_number", label: app.$t("turningNumber"), format: "number", width: 0.8 },
        { key: "date", label: app.$t("date"), format: "date", width: 1 },
        {
          key: "type",
          label: app.$t("documentType"),
          width: 1.1,
          value: function (r) {
            return app.$t(r.type) + " · " + (r.doc_type == "always" ? app.$t("always") : app.$t("temporary"));
          },
        },
        {
          key: "submit_doc_way",
          label: app.$t("submitDocWay"),
          width: 1,
          defaultVisible: false,
          value: function (r) {
            return r.ref_type == null ? app.$t("manual") : app.$t(r.ref_type);
          },
        },
        { key: "content", label: app.$t("content"), width: 3 },
        {
          key: "debtor",
          label: app.$t("debtor"),
          format: "money",
          sum: true,
          width: 1.3,
          value: function (r) { return r.price ? r.price.sum_debtor : 0; },
        },
        {
          key: "creditor",
          label: app.$t("creditor"),
          format: "money",
          sum: true,
          width: 1.3,
          value: function (r) { return r.price ? r.price.sum_creditor : 0; },
        },
        {
          key: "status",
          label: app.$t("status"),
          width: 1,
          defaultVisible: false,
          value: function (r) {
            return r.last_status && r.last_status.status ? r.last_status.status.name : "";
          },
        },
        {
          key: "branch",
          label: app.$t("branch"),
          width: 1,
          defaultVisible: false,
          value: function (r) { return r.branch ? r.branch.name : ""; },
        },
        { key: "user", label: app.$t("registerar"), width: 1.2, defaultVisible: false, value: user },
      ];
    },
    printSource() {
      var app = this;
      return {
        currentRows: function () { return app.items || []; },
        total: function () { return app.totalItems || 0; },
        fetchPage: function (page, perPage) {
          var options = Object.assign({}, app.tableOptions, { page: page, itemsPerPage: perPage });
          return app.$axios
            .get(app.$endpoint + "accounting", { params: app.listParams(page, options) })
            .then(function (res) {
              var d = res.data;
              return { rows: d.data || [], lastPage: d.last_page || 1, total: d.total || 0 };
            });
        },
      };
    },
    printSubtitle() {
      return this.activeFilterChips
        .map(function (c) { return c.label + (c.value ? ": " + c.value : ""); })
        .join(" · ");
    },
    pageTotals() {
      var t = { debtor: 0, creditor: 0, diff: 0, locked: 0, temporary: 0 };
      (this.items || []).forEach(function (d) {
        t.debtor += d.price ? parseFloat(d.price.sum_debtor) || 0 : 0;
        t.creditor += d.price ? parseFloat(d.price.sum_creditor) || 0 : 0;
        if (d.lock) t.locked++;
        if (d.doc_type !== "always") t.temporary++;
      });
      t.diff = Math.abs(t.debtor - t.creditor);
      return t;
    },
    activeFilterChips() {
      var app = this;
      var has = function (v) {
        if (Array.isArray(v)) return v.length > 0;
        return v !== null && v !== undefined && v !== "" && v !== "0" && v !== 0;
      };
      var show = function (v) {
        if (Array.isArray(v)) return v.length > 1 ? String(v.length) : show(v[0]);
        if (v && typeof v === "object") return v.name || v.text || "";
        return typeof v === "number" || typeof v === "string" ? String(v) : "";
      };
      var pick = function (items, v, key) {
        var arr = Array.isArray(v) ? v : [v];
        return arr
          .map(function (x) {
            var f = (items || []).find(function (i) {
              return i.value === x;
            });
            return f ? f[key || "text"] : "";
          })
          .filter(Boolean)
          .join("، ");
      };
      var defs = [
        ["fromDate", app.$t("from") + " " + app.$t("date")],
        ["toDate", app.$t("until") + " " + app.$t("date")],
        ["fromNumber", app.$t("from") + " " + app.$t("number")],
        ["toNumber", app.$t("until") + " " + app.$t("number")],
        ["fromAmount", app.$t("fromAmount")],
        ["toAmount", app.$t("toAmount")],
        ["fromTime", app.$t("from-hour")],
        ["toTime", app.$t("to-hour")],
        ["type", app.$t("natureAccount"), pick(app.typeItems, app.type)],
        ["submitDocWay", app.$t("submitDocWay"), pick(app.submitDocWays, app.submitDocWay, "label")],
        ["lockStatus", app.$t("lockStatusDocuments"), pick(app.lockStatusItems, app.lockStatus)],
        ["given", app.$t("given")],
        ["fin4", app.$t("detail")],
        ["fin5", app.$t("fin5")],
        ["fin6", app.$t("fin6")],
        ["branch", app.$t("branch")],
        ["registerar", app.$t("registerar")],
        ["tag", app.$t("tag")],
        ["sellStatus", app.$t("status")],
      ];
      return defs
        .filter(function (d) {
          return has(app[d[0]]);
        })
        .map(function (d) {
          return { key: d[0], label: d[1], value: d.length > 2 ? d[2] : show(app[d[0]]) };
        });
    },
    showSelect() {
      if (!this.isMobile) {
        return true;
      } else {
        return false;
      }
    },
    accountingListColumnPersistKey() {
      return (
        "accounting-document-list-columns-" + (this.$route.name || "accountingList")
      );
    },
    accountingListLockedColumns() {
      // Fixed sticky block: select + actions + id + document number (no unpin/drag)
      var locked = ["actions", "id", "number"];
      return this.showSelect ? ["__select"].concat(locked) : locked;
    },
    accountingListColumnPlugins() {
      // show-select inserts a leading checkbox <th>; pad visibleHeaders so
      // pin/resize/drag index mapping stays aligned with thead cells.
      var selectHeader = {
        value: "__select",
        text: "",
        width: 32,
        sortable: false,
        pinned: false,
        originalIndex: -1,
      };
      var visible = this.showSelect
        ? [selectHeader].concat(this.shownHeaders)
        : this.shownHeaders;
      return {
        headers: this.headers,
        visibleHeaders: visible,
        locked: this.accountingListLockedColumns,
        anchor: "number",
        minWidth: 60,
        persistKey: this.accountingListColumnPersistKey,
      };
    },
    submitDocWays() {
      return [
        { label: this.$t("manual"), value: "manual" },
        { label: this.$t("auto-checks"), value: "checks" },
        { label: this.$t("auto-costIncome"), value: "costIncome" },
        { label: this.$t("auto-entrepot"), value: "entrepot" },
        { label: this.$t("auto-invoice"), value: "invoice" },
        { label: this.$t("auto-payments"), value: "payments" },
        { label: this.$t("auto-transfer"), value: "transfer" },
      ];
    },
  },
  watch: {
    refId(val, oldVal) {
      // this.items = []
      this.getItems();
    },
    model() {
      this.$emit("input", this.model);
    },
    selected() {
      this.$nextTick(this.plMeasure);
      if (!this.selected.length) return;
      this.$emit("input", this.selected.text);
      this.model = this.selected[0].text;
      this.temp = this.selected[0].text;
      this.dialog = false;
    },
    search() {
      var app = this;
      app.page = 1;
      app.loading = true;
      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        app.getItems();
      }, 1000);
    },
    tableOptions: {
      handler() {
        var app = this;
        app.sortBy = [];
        (app.tableOptions.sortBy || []).map(function (value, key) {
          const index = app.headers.findIndex((h) => h.value === value);
          app.sortBy.push(app.headers[index].table);
        });
        this.getItems();
      },
      deep: true,
    },
    sortBy() {
      const index = this.headers.findIndex((h) => h.value === this.sortBy);
      if (index > -1) {
        this.sortBy = this.headers[index].table;
      }
    },
  },
  activated() {
    // this.getItems()
  },
  beforeMount() {
    if (this.$route.name === "conflictingDocuments") {
      this.conflictingDocuments = "active";
    } else {
      this.conflictingDocuments = null;
    }
    this.typeItems = [
      {
        value: "initial",
        text: this.$t("initial"),
      },
      {
        value: "general",
        text: this.$t("general"),
      },
    ];
    this.headers = [
      {
        text: this.$t("#"),
        align: "center",
        sortable: false,
        value: "actions",
        width: 42,
        class: "pl-action-col",
        cellClass: "pl-action-col",
      },
      {
        text: this.$t("id"),
        sortable: true,
        value: "id",
        table: "doc_header.id",
      },
      {
        text: this.$t("document-number"),
        sortable: true,
        value: "number",
        table: "doc_header.number",
      },
      {
        text: this.$t("turningNumber"),
        sortable: true,
        value: "turning_number",
        table: "doc_header.turning_number",
        width: 20,
      },
      {
        text: this.$t("documentType"),
        sortable: true,
        value: "type",
        table: "doc_header.type",
      },
      {
        text: this.$t("submitDocWay"),
        sortable: false,
        value: "submit_doc_way",
      },
      {
        text: this.$t("content"),
        sortable: true,
        value: "content",
        table: "doc_header.content",
      },
      {
        text: this.$t("date"),
        sortable: true,
        value: "date",
        table: "doc_header.date",
      },
      {
        text: this.$t("debtor"),
        sortable: false,
        value: "price.sum_debtor",
      },
      {
        text: this.$t("creditor"),
        sortable: false,
        value: "price.sum_creditor",
      },
      {
        text: this.$t("status"),
        sortable: true,
        value: "last_status.status_id",
        table: "doc_header.buyer_id",
        width: 50,
      },
      {
        text: this.$t("branch"),
        sortable: false,
        value: "branch.name",
        width: 50,
      },
      {
        text: this.$t("tag"),
        sortable: false,
        value: "tags",
      },
      {
        text: this.$t("registerar"),
        sortable: false,
        value: "user.name",
      },
    ];
    reorderPinnedHeaders(this.headers, {
      locked: this.accountingListLockedColumns,
      anchor: "number",
    });
    this.loadAccountingListColumnSettings();
    if (this.input) {
      this.model = this.input;
    }
  },
  mounted() {},
  methods: {
    loadAccountingListColumnSettings() {
      var app = this;
      if (!app.headers || !app.headers.length) return;
      loadColumnSettings(app, app.accountingListColumnPersistKey).then(function (
        snapshot
      ) {
        if (!snapshot || !snapshot.length) return;
        applyColumnSettings(app.headers, snapshot, {
          locked: app.accountingListLockedColumns,
          anchor: "number",
        });
        app.$forceUpdate();
      });
    },
    openDocView(item) {
      this.docViewItem = item;
      this.docViewOpen = true;
    },
    canRemoveDoc(item) {
      return !!(
        item &&
        item.doc_type == "temporary" &&
        this.$store.state.permissions &&
        this.$store.state.permissions.includes("removeDocumentAccounting")
      );
    },
    docLink(item) {
      return item ? this.$baseURL + "/accounting/edit/" + item.id : "";
    },
    copied() {
      this.snakbarText = this.$t("accDocListLinkCopied");
      this.snakbarColor = "green";
      this.snakbar = true;
    },
    rowClass(item) {
      return item.lock ? "pl-row--muted" : "";
    },
    resetFilters() {
      var app = this;
      [
        "fromDate", "toDate", "fromNumber", "toNumber", "fromAmount", "toAmount",
        "fromTime", "toTime", "type", "lockStatus", "given", "fin4", "fin5",
        "fin6", "branch", "registerar", "tag", "sellStatus",
      ].forEach(function (k) {
        app[k] = null;
      });
      app.submitDocWay = [];
    },
    clearFilterField(key) {
      this[key] = key === "submitDocWay" ? [] : null;
      this.getItems();
    },
    clearAllFilters() {
      this.resetFilters();
      this.search = null;
      this.getItems();
    },
    convertDocToTempConfirmation(item) {
      this.askPermissionDialog = true;
      this.docId = item.id;
    },
    convertDocToTemp() {
      var app = this;
      app.loading = true;
      let idsArray = [this.docId];
      app.$axios
        .post(this.$endpoint + "document/change-doc-type", {
          ids: idsArray,
        })
        .then((r) => {
          app.askPermissionDialog = false;
          app.snakbarText = r.data.message;
          app.snakbarColor = "green";
          app.snakbar = true;
          app.docId = null;
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    convertDocToTempConfirmationGroup(items) {
      this.askPermissionDialog = true;
      this.docIds = items.map((item) => item.id);
    },
    convertDocToTempSets() {
      var app = this;
      app.loading = true;
      app.$axios
        .post(this.$endpoint + "document/change-doc-type", {
          ids: this.docIds,
        })
        .then((r) => {
          app.askPermissionDialog = false;
          app.snakbarText = r.data.message;
          app.snakbarColor = "green";
          app.snakbar = true;
          app.docIds = null;
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    resolveConvertFunc() {
      if (this.docId != null) {
        this.convertDocToTemp();
      } else {
        this.convertDocToTempSets();
      }
    },
    openAggregateDialog() {
      this.aggregateDialog = true;
    },
    openAggregatePrint(params) {
      var app = this;
      app.aggregateLoading = true;
      app.$axios
        .get(this.$endpoint + "accounting/aggregate-print", {
          params: {
            Print: "active",
            date_type: params.method,
            fromDate: params.fromDate,
            toDate: params.toDate,
            fromMonth: params.fromMonth,
            toMonth: params.toMonth,
            group_type: params.Priority,
          },
        })
        .then((res) => {
          res = res.data;
          app.$router.push({
            name: "reportViewer",
            params: { dataProp: res, reportProp: "totalDocument" },
          });
          app.aggregateDialog = false;
          app.printItems = [];
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.aggregateLoading = false));
    },
    openDialogPrint(id) {
      this.printItems.push(id);
      this.printDialog = true;
    },
    groupPrint() {
      var app = this;
      this.selected.map(function (value) {
        app.printItems.push(value.id);
      });
      this.printDialog = true;
    },
    openPrint() {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "accounting", {
          params: {
            print: "active",
            id: app.printItems,
            type: app.printType,
          },
        })
        .then((res) => {
          app.$router.push({
            name: "reportViewer",
            params: { dataProp: res.data, reportProp: "document2" },
          });
          app.printItems = [];
          // var routeData = app.$router.resolve({ name: 'reportViewer', params: { dataProp: res } })
          // window.open(routeData.href, '_blank')
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    openComponent(component, id) {
      const id_ = id;
      const componentData = component;
      this.dialogData = {
        name: componentData,
        id: id_,
      };
      this.innerComponent = true;
    },
    openDeleteDialog(item) {
      this.deleteItem = item;
      this.deletDialog = true;
    },
    openTaskDialog(item) {
      this.refId = item.rel_id;
      this.taskId = item.id;
      this.taskDialog = true;
    },
    isEnabled(slot) {
      return this.enabled === slot;
    },
    copy(item) {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "accounting/" + item.id + "/edit", {
          params: {
            id: item.id,
          },
        })
        .then((res) => {
          res = res.data;
          if (res.article) {
            app.$router.push({ name: "accounting", params: { itemsProp: res } });
          }
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    edit(item) {
      if (item.lock) {
        return false;
      }
      this.$router.push({
        name: "accountingEdit",
        params: { id: String(item.id), tabName: item.number },
      });
    },
    openUploadDialog(item) {
      if (this.$root) {
        this.$root.$emit("open-user-file-process", {
          type: "accountingDocument",
        });
      }
    },
    // Query params shared by the table and the print module.
    listParams(page, options) {
      var app = this;
      return {
        user_id: app.registerar,
        search: app.search,
        page: page,
        refId: app.refId,
        type: app.type,
        conflictingDocuments: app.conflictingDocuments,
        branch: app.branch,
        fromNumber: app.fromNumber,
        toNumber: app.toNumber,
        fromDate: app.fromDate,
        toDate: app.toDate,
        sortBy: app.sortBy,
        options: options,
        lockStatus: app.lockStatus,
        tag: app.tag,
        status: app.sellStatus,
        given: app.given,
        fin4: app.fin4,
        fin5: app.fin5,
        fin6: app.fin6,
        fromAmount: app.fromAmount,
        toAmount: app.toAmount,
        fromTime: app.fromTime,
        toTime: app.toTime,
        ref_type: app.submitDocWay,
      };
    },
    openPrintStudio() {
      this.printStudio = true;
    },
    getItems() {
      var app = this;
      app.loading = true;
      app.filter = false;
      app.$axios
        .get(this.$endpoint + "accounting", {
          params: app.listParams(
            app.allRows ? app.excelPage : app.tableOptions.page || 1,
            app.tableOptions
          ),
        })
        .then((res) => {
          res = res.data;
          if (!app.allRows) {
            app.items = res.data;
            app.totalItems = res.total;
            app.pageCount = res.last_page;
            app.from = res.from;
            app.creatExcelData(res.data);
          } else {
            app.generateExcellForAllData(res.data, res.last_page);
          }
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    remove() {
      var app = this;
      if (!app.deleteItem) return false;
      app.deletDialog = false;
      app.loading = true;
      app.$axios
        .delete(this.$endpoint + "accounting/" + this.deleteItem.id)
        .then((res) => {
          res = res.data;
          app.deletDialog = false;
          app.deleteItem = null;
          app.getItems();
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    removegroup() {
      var app = this;
      app.loading = true;
      app.deletDialog = false;
      var selected = [];
      app.selected.map(function (value, key) {
        selected.push({ id: value.id });
      });
      app.$axios
        .get(this.$endpoint + "accounting", {
          params: {
            deleteItems: selected,
          },
        })
        .then((res) => {
          res = res.data;
          app.deletDialog = false;
          app.deleteItem = null;
          app.selected = [];
          app.getItems();
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    lock(item, type) {
      if (this.$store.state.userCurrentPlan != null) {
        const lockItem = this.$store.state.userCurrentPlan.plan.options.find(
          (item) => item.key == "lockSystem"
        );
        if (lockItem && lockItem.value == "deactive") {
          this.$store.commit("SET_PLAN_DIALOG", true);
          this.$router.replace("/");
        } else {
          this.selected = [item];
          this.lockGroup(type);
        }
      }
    },
    lockGroup(type) {
      var app = this;
      app.loading = true;
      var selected = [];
      app.selected.map(function (value, key) {
        selected.push(value.id);
      });
      app.$axios
        .get(this.$endpoint + "accounting", {
          params: {
            lock: "active",
            id: selected,
            type: type,
          },
        })
        .then((res) => {
          res = res.data;
          app.snakbarText = this.$t("opertationSuccess");
          app.snakbarColor = "green";
          app.snakbar = true;
          app.getItems();
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(function () {
          app.loading = false;
          app.selected = [];
        });
    },
    // add unlock group send type
    unlockGroup(type = "unlock") {
      this.lockGroup(type);
    },
    creatExcelData(data, type = "page") {
      var app = this;
      if (type === "page") {
        app.excelItems = [];
      }
      data.map(function (d) {
        app.excelItems.push({
          content: d.content,
          date: d.date,
          number: d.number,
          daily_number: d.daily_number,
          turning_number: d.turning_number,
          branch: d.branch ? d.branch.name : "",
          doc_type: app.$t(d.doc_type),
          debtor: d.price ? d.price.sum_debtor : "",
          creditor: d.price ? d.price.sum_creditor : "",
          user: d.user ? d.user.name : "",
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .v-data-table table thead tr th,
::v-deep .v-data-table table tbody tr td {
  white-space: nowrap;
}
::v-deep
  .v-text-field--outlined.v-input--dense.v-text-field--outlined
  > .v-input__control
  > .v-input__slot {
  min-height: 32px !important;
}
::v-deep .v-chip__content {
  font-size: 9px;
}

/* Desktop view uses the shared pro-list kit (namespaced under .pl) */
@import "~@/sass/design-system/list/pro-list";
/* Accounting-only bits */
.pl .acc-type {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>
